import {
  CLIP_PLACEMENT_OVERLAY,
  getClipPlaybackRate,
  VIDEO_EDITOR_MIN_CLIP_DURATION,
  VIDEO_EDITOR_MIN_PLAYBACK_RATE,
  VIDEO_EDITOR_TRACK_TYPE,
} from "./constants";
import { clipHasCanvasLayout } from "./clipProperties";
import { getClipTimelineDuration } from "./projectSchema";
import {
  applyPackedLanesToEdl,
  countOverlappingClips,
  getClipInterval,
  packClipsIntoLanes,
} from "./timelineLanePacking";

const isOverlayLaneClip = (clip) =>
  clip?.type === VIDEO_EDITOR_TRACK_TYPE.IMAGE ||
  clip?.type === VIDEO_EDITOR_TRACK_TYPE.VIDEO ||
  clip?.type === VIDEO_EDITOR_TRACK_TYPE.AUDIO;

export const getClipSourceDuration = (clip) => {
  if (clip?.sourceDuration != null && Number.isFinite(Number(clip.sourceDuration))) {
    return Math.max(Number(clip.sourceDuration), VIDEO_EDITOR_MIN_CLIP_DURATION);
  }

  if (
    clip?.type === VIDEO_EDITOR_TRACK_TYPE.VIDEO ||
    clip?.type === VIDEO_EDITOR_TRACK_TYPE.AUDIO
  ) {
    const outPoint = Number(clip?.outPoint) || 0;
    const inPoint = Number(clip?.inPoint) || 0;
    return Math.max(outPoint - inPoint, VIDEO_EDITOR_MIN_CLIP_DURATION);
  }

  return null;
};

export const isSourceBoundedClip = (clip) =>
  clip?.type === VIDEO_EDITOR_TRACK_TYPE.VIDEO ||
  clip?.type === VIDEO_EDITOR_TRACK_TYPE.AUDIO;

export const clampClipTrimToSource = (clip) => {
  if (!isSourceBoundedClip(clip)) {
    return clip;
  }

  const sourceDuration = getClipSourceDuration(clip);
  if (!sourceDuration) {
    return clip;
  }

  const inPoint = Math.max(0, Number(clip.inPoint) || 0);
  const maxOutPoint = sourceDuration;
  const minOutPoint = inPoint + VIDEO_EDITOR_MIN_CLIP_DURATION;
  const outPoint = Math.max(
    minOutPoint,
    Math.min(Number(clip.outPoint) || maxOutPoint, maxOutPoint)
  );

  return {
    ...clip,
    inPoint,
    outPoint,
    playbackRate: getClipPlaybackRate({ clip }),
  };
};

const clampPlaybackRateForStretch = ({ sourceWindow, timelineDuration }) => {
  const safeSourceWindow = Math.max(sourceWindow, VIDEO_EDITOR_MIN_CLIP_DURATION);
  const safeTimelineDuration = Math.max(
    timelineDuration,
    VIDEO_EDITOR_MIN_CLIP_DURATION
  );

  return Math.min(
    1,
    Math.max(VIDEO_EDITOR_MIN_PLAYBACK_RATE, safeSourceWindow / safeTimelineDuration)
  );
};

export const clampTimelineResize = ({ clip, start, end, dir }) => {
  const safeStart = Math.max(0, Number(start) || 0);
  const requestedDuration = Math.max(
    VIDEO_EDITOR_MIN_CLIP_DURATION,
    (Number(end) || 0) - safeStart
  );

  if (!isSourceBoundedClip(clip)) {
    const inPoint = Number(clip.inPoint) || 0;
    return {
      position: safeStart,
      inPoint,
      outPoint: inPoint + requestedDuration,
    };
  }

  const sourceDuration = getClipSourceDuration(clip);
  const currentInPoint = Number(clip.inPoint) || 0;
  const currentOutPoint = Number(clip.outPoint) || sourceDuration;

  if (dir === "left") {
    const originalEnd =
      (Number(clip.position) || 0) + getClipTimelineDuration({ clip });
    const nextStart = Math.min(
      safeStart,
      originalEnd - VIDEO_EDITOR_MIN_CLIP_DURATION
    );
    const nextDuration = Math.max(
      VIDEO_EDITOR_MIN_CLIP_DURATION,
      originalEnd - nextStart
    );
    const availableSource = Math.max(
      currentOutPoint,
      VIDEO_EDITOR_MIN_CLIP_DURATION
    );

    if (nextDuration <= availableSource) {
      return {
        position: Math.max(0, nextStart),
        inPoint: Math.max(0, currentOutPoint - nextDuration),
        outPoint: currentOutPoint,
        playbackRate: 1,
      };
    }

    const playbackRate = clampPlaybackRateForStretch({
      sourceWindow: availableSource,
      timelineDuration: nextDuration,
    });
    const stretchedDuration = availableSource / playbackRate;

    return {
      position: Math.max(0, originalEnd - stretchedDuration),
      inPoint: 0,
      outPoint: currentOutPoint,
      playbackRate,
    };
  }

  const position = Number(clip.position) || 0;
  const availableSource = Math.max(
    sourceDuration - currentInPoint,
    VIDEO_EDITOR_MIN_CLIP_DURATION
  );

  if (requestedDuration <= availableSource) {
    return {
      position,
      inPoint: currentInPoint,
      outPoint: currentInPoint + requestedDuration,
      playbackRate: 1,
    };
  }

  const playbackRate = clampPlaybackRateForStretch({
    sourceWindow: availableSource,
    timelineDuration: requestedDuration,
  });

  return {
    position,
    inPoint: currentInPoint,
    outPoint: currentInPoint + availableSource,
    playbackRate,
  };
};

export const getTracksByType = ({ edl, type }) => {
  const order = edl?.tracks?.order || [];
  return order
    .map((trackId) => edl?.tracks?.byId?.[trackId])
    .filter((track) => track?.type === type);
};

export const getImageTracksOrdered = ({ edl }) =>
  getTracksByType({ edl, type: VIDEO_EDITOR_TRACK_TYPE.IMAGE });

export const getAudioTracksOrdered = ({ edl }) =>
  getTracksByType({ edl, type: VIDEO_EDITOR_TRACK_TYPE.AUDIO });

export const getVideoTracksOrdered = ({ edl }) =>
  getTracksByType({ edl, type: VIDEO_EDITOR_TRACK_TYPE.VIDEO });

export const clipsOverlapOnTimeline = ({ leftClip, rightClip }) => {
  const left = getClipInterval(leftClip);
  const right = getClipInterval(rightClip);
  return left.start < right.end && right.start < left.end;
};

export const getOverlayClips = ({ edl, type }) =>
  Object.values(edl?.clips?.byId || {}).filter(
    (clip) => clip?.type === type
  );

export const getClipPackingLaneIndex = ({ edl, clipId }) => {
  const clip = edl?.clips?.byId?.[clipId];
  if (!clip) {
    return 0;
  }
  if (Number.isInteger(clip.layerIndex)) {
    return Math.max(0, clip.layerIndex);
  }
  return 0;
};

export const reflowOverlayLanes = ({
  edl,
  type,
  preferredByClipId = {},
}) => {
  const clips = getOverlayClips({ edl, type });
  const packedLanes = packClipsIntoLanes({
    clips,
    preferredByClipId,
  });
  return applyPackedLanesToEdl({ edl, type, packedLanes });
};

export const reflowAllOverlayLanes = ({ edl, preferredByClipId = {} }) => {
  const withImages = reflowOverlayLanes({
    edl,
    type: VIDEO_EDITOR_TRACK_TYPE.IMAGE,
    preferredByClipId,
  });
  const withVideo = reflowOverlayLanes({
    edl: withImages,
    type: VIDEO_EDITOR_TRACK_TYPE.VIDEO,
    preferredByClipId,
  });
  return reflowOverlayLanes({
    edl: withVideo,
    type: VIDEO_EDITOR_TRACK_TYPE.AUDIO,
    preferredByClipId,
  });
};

export const removeClipFromTrack = ({ edl, clipId }) => {
  const clip = edl?.clips?.byId?.[clipId];
  if (!clip?.trackId) {
    return edl;
  }

  const track = edl.tracks.byId[clip.trackId];
  if (!track) {
    return edl;
  }

  return {
    ...edl,
    tracks: {
      ...edl.tracks,
      byId: {
        ...edl.tracks.byId,
        [clip.trackId]: {
          ...track,
          clipIds: (track.clipIds || []).filter((id) => id !== clipId),
        },
      },
    },
  };
};

const withUpdatedClip = ({ edl, clipId, updates }) => ({
  ...edl,
  clips: {
    byId: {
      ...edl.clips.byId,
      [clipId]: {
        ...edl.clips.byId[clipId],
        ...updates,
      },
    },
  },
});

export const updateClipInEdl = withUpdatedClip;

export const updateClipsInEdl = ({ edl, updatesByClipId = {} }) => {
  const nextClipsById = { ...edl.clips.byId };
  Object.entries(updatesByClipId).forEach(([clipId, updates]) => {
    if (!nextClipsById[clipId]) {
      return;
    }
    nextClipsById[clipId] = {
      ...nextClipsById[clipId],
      ...updates,
    };
  });

  return {
    ...edl,
    clips: {
      byId: nextClipsById,
    },
  };
};

export const assignClipToLane = ({
  edl,
  clipId,
  position,
  preferredLaneIndex,
}) => {
  const clip = edl?.clips?.byId?.[clipId];
  if (!isOverlayLaneClip(clip)) {
    return edl;
  }

  const updates = {
    position: Math.max(0, Number(position) || 0),
    ...(clipHasCanvasLayout(clip) ? { placement: CLIP_PLACEMENT_OVERLAY } : {}),
  };

  return reflowOverlayLanes({
    edl: withUpdatedClip({ edl, clipId, updates }),
    type: clip.type,
    preferredByClipId: Number.isInteger(preferredLaneIndex)
      ? { [clipId]: preferredLaneIndex }
      : {},
  });
};

export const findRowForClipInTimelineRows = ({ rows, clipId }) => {
  for (const row of rows || []) {
    if (row.actions?.some((action) => action.id === clipId)) {
      return row;
    }
  }

  return null;
};

export const resolveTimelineResizeEnd = ({ edl, clipId, start, end, dir }) => {
  const clip = edl?.clips?.byId?.[clipId];
  if (!clip) {
    return edl;
  }

  const updates = clampTimelineResize({ clip, start, end, dir });
  const nextEdl = withUpdatedClip({ edl, clipId, updates });
  if (!isOverlayLaneClip(clip)) {
    return nextEdl;
  }

  return assignClipToLane({
    edl: nextEdl,
    clipId,
    position: updates.position,
    preferredLaneIndex: getClipPackingLaneIndex({ edl, clipId }),
  });
};

export const resolveTimelineGroupMoveEnd = ({
  edl,
  clipIds = [],
  primaryClipId,
  deltaTime = 0,
  preferredLaneIndex,
}) => {
  const uniqueClipIds = [...new Set(clipIds.filter(Boolean))];
  if (!uniqueClipIds.length) {
    return edl;
  }

  const updatesByClipId = {};
  uniqueClipIds.forEach((clipId) => {
    const clip = edl?.clips?.byId?.[clipId];
    if (!clip) {
      return;
    }
    updatesByClipId[clipId] = {
      position: Math.max(0, (Number(clip.position) || 0) + deltaTime),
    };
  });

  let nextEdl = updateClipsInEdl({ edl, updatesByClipId });
  const primaryClip =
    edl?.clips?.byId?.[primaryClipId] || edl?.clips?.byId?.[uniqueClipIds[0]];
  const originLane = getClipPackingLaneIndex({
    edl,
    clipId: primaryClip?.id,
  });
  const laneDelta = Number.isInteger(preferredLaneIndex)
    ? preferredLaneIndex - originLane
    : 0;

  const typesToReflow = new Set(
    uniqueClipIds
      .map((clipId) => nextEdl.clips.byId[clipId]?.type)
      .filter(
        (type) =>
          type === VIDEO_EDITOR_TRACK_TYPE.IMAGE ||
          type === VIDEO_EDITOR_TRACK_TYPE.AUDIO ||
          type === VIDEO_EDITOR_TRACK_TYPE.VIDEO
      )
  );

  typesToReflow.forEach((type) => {
    const typePreferred = {};
    uniqueClipIds.forEach((clipId) => {
      const clip = nextEdl.clips.byId[clipId];
      if (clip?.type !== type) {
        return;
      }
      const origin = getClipPackingLaneIndex({ edl, clipId });
      typePreferred[clipId] =
        type === primaryClip?.type ? Math.max(0, origin + laneDelta) : origin;
    });
    nextEdl = reflowOverlayLanes({
      edl: nextEdl,
      type,
      preferredByClipId: typePreferred,
    });
  });

  return nextEdl;
};

export const getOverlayConcurrency = ({ edl, type, clip }) =>
  countOverlappingClips({
    clips: getOverlayClips({ edl, type }),
    clip,
  });
